import { useEffect, useRef, useState } from 'react'
import type { Category, Transaction } from '../db/database'
import type { PlannedTag } from '../db/budget'
import { formatSigned } from '../lib/money'
import { formatDay, formatFullDate } from '../lib/dates'
import { Icon, type IconName } from './Icon'
import './TransactionDetailSheet.css'

/**
 * Le détail d'une opération, en LECTURE.
 *
 * Toucher une ligne ouvre ce qu'elle contient, pas un formulaire : la liste est
 * tronquée par nature — un nom long, une description sur trois lignes n'y
 * tiennent pas — et vouloir relire n'est pas vouloir corriger. Modifier reste à
 * un geste, mais c'est un geste choisi.
 */
export function TransactionDetailSheet(
  { transaction, category, plan, onClose, onEdit, onDelete }: {
    transaction: Transaction
    category?: Category
    plan?: PlannedTag
    onClose: () => void
    onEdit: (transaction: Transaction) => void
    onDelete: (transaction: Transaction, label: string) => void
  },
) {
  const [menu, setMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const income = transaction.direction === 'income'
  const label = transaction.title ?? category?.name ?? transaction.note ?? 'Sans catégorie'

  // Même piège que dans la liste : fermer en phase de capture retirerait les
  // boutons du DOM avant que React ne déclenche leur `onClick`.
  useEffect(() => {
    if (!menu) return
    const fermer = (event: MouseEvent) => {
      if (menuRef.current?.contains(event.target as Node)) return
      setMenu(false)
    }
    window.addEventListener('click', fermer, { capture: true })
    return () => window.removeEventListener('click', fermer, { capture: true })
  }, [menu])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const origine = plan?.mode === 'once' ? 'Payé en une fois'
    : plan?.mode === 'daily'
      ? plan.total > 1 ? `Journée ${plan.index} sur ${plan.total}` : 'Journée prévue'
      : null

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true"
         aria-label={`Détails de ${label}`}>
      <div className="sheet detail-sheet">
        <header className="detail-head">
          <button type="button" onClick={onClose} aria-label="Retour">
            <Icon name="arrowLeft" size={20} />
          </button>
          <h2>Détails</h2>
          <div className="detail-menu" ref={menuRef}>
            <button type="button" aria-label="Actions" aria-expanded={menu}
                    onClick={() => setMenu((m) => !m)}>
              <Icon name="more" size={20} />
            </button>
            {menu && (
              <div className="row-actions" role="menu">
                <button type="button" role="menuitem"
                        onClick={() => { setMenu(false); onEdit(transaction) }}>
                  <Icon name="pencil" size={16} /> Modifier
                </button>
                <button type="button" role="menuitem" className="is-danger"
                        onClick={() => { setMenu(false); onDelete(transaction, label) }}>
                  <Icon name="trash" size={16} /> Supprimer
                </button>
              </div>
            )}
          </div>
        </header>

        {/* En ligne et non empilé : centré, ce résumé mangeait la moitié de la
            hauteur de la feuille et repoussait les informations sous le pli.
            L'icône situe, le nom et le montant se lisent d'un seul regard à sa
            droite. */}
        <div className="detail-hero">
          <span className={`detail-icon ${income ? 'is-income' : 'is-expense'}`}>
            <Icon name={(category?.icon as IconName) ?? 'other'} size={24} />
          </span>
          <div className="detail-hero-body">
            <p className="detail-name">{label}</p>
            <output className={`detail-amount tabular ${income ? 'is-income' : 'is-expense'}`}>
              {formatSigned(transaction.amount, transaction.direction)}
            </output>
            <p className="detail-meta">
              <span className={`detail-kind ${income ? 'is-income' : 'is-expense'}`}>
                {income ? 'Revenu' : 'Dépense'}
              </span>
              {/* La date en toutes lettres, sans heure : `operation_date` n'en
                  porte pas, et l'horloge de saisie n'est pas celle de la
                  dépense. */}
              <span className="detail-date">
                {formatFullDate(transaction.operation_date)}
              </span>
            </p>
          </div>
        </div>

        <dl className="detail-facts">
          <h3>Informations</h3>
          <div><dt>Type</dt><dd className={income ? 'is-income' : 'is-expense'}>
            {income ? 'Revenu' : 'Dépense'}
          </dd></div>
          <div><dt>Catégorie</dt><dd>{category?.name ?? 'Sans catégorie'}</dd></div>
          {transaction.title && <div><dt>Titre</dt><dd>{transaction.title}</dd></div>}
          <div><dt>Date</dt><dd>{formatDay(transaction.operation_date)}</dd></div>
          {origine && <div><dt>Origine</dt><dd>{origine}</dd></div>}
          {transaction.note && (
            // Sur sa propre ligne : une description tient sur plusieurs lignes,
            // et la coincer dans une colonne de droite la rend illisible.
            <div className="is-long">
              <dt>Description</dt>
              <dd>{transaction.note}</dd>
            </div>
          )}
        </dl>

        <button type="button" className="detail-edit" onClick={() => onEdit(transaction)}>
          <Icon name="pencil" size={16} /> Modifier cette opération
        </button>
      </div>
    </div>
  )
}
